"use client";

import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { createClient } from "@/utils/supabase/client";
import { ChatThread } from "@/types/chat";
import { RealtimePostgresChangesPayload } from "@supabase/supabase-js";
import { usePathname, useSearchParams } from "next/navigation";
import { useSubscription } from "@/hooks/useSubscription";

interface ThreadsContextType {
  threads: ChatThread[];
  isLoading: boolean;
  refresh: () => Promise<void>;
  locationIds: string[];
  setLocationIds: (ids: string[]) => void;
  currentThreadId?: string;
  setCurrentThreadId: (id: string | undefined) => void;
  openThread: (threadId: string, primaryLocationId?: string | null) => void;
  /** Permanently delete a thread (messages/locations cascade in the DB). Throws on failure. */
  deleteThread: (threadId: string) => Promise<void>;
}

const ThreadsContext = createContext<ThreadsContextType | undefined>(undefined);



export function ThreadsProvider({ children }: { children: React.ReactNode }) {
  const [threads, setThreads] = useState<ChatThread[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [locationIds, setLocationIdsInternal] = useState<string[]>([]);
  
  // Wrapper to prevent unnecessary updates and filter out invalid IDs
  const setLocationIds = useCallback((ids: string[]) => {
    // Filter out invalid IDs before setting
    const validIds = ids.filter(id => id && id !== "null" && id !== "undefined" && id.trim() !== "");
    
    setLocationIdsInternal(prev => {
      // Check if arrays are actually different
      if (prev.length === validIds.length && prev.every((id, index) => id === validIds[index])) {
        return prev; // No change needed
      }
      console.log('[ThreadsContext] Setting location IDs:', prev, '->', validIds);
      return validIds;
    });
  }, []);
  const [currentThreadId, setCurrentThreadIdInternal] = useState<string | undefined>();

  // Wrapper to prevent unnecessary updates
  const setCurrentThreadId = useCallback((id: string | undefined) => {
    setCurrentThreadIdInternal(prev => {
      if (prev === id) return prev; // No change needed
      console.log('[ThreadsContext] Setting thread ID:', prev, '->', id);
      return id;
    });
  }, []);

  // Monotonic counter so a slow thread_locations fetch for an earlier
  // openThread can never clobber the context of a later one.
  const openThreadRequestRef = React.useRef(0);

  // Open a thread AND restore its location context (thread_locations
  // junction, falling back to the primary location_id). Every entry point
  // that switches threads — sidebar row, notification bell — must go through
  // here, otherwise the location chips keep showing the previous thread's
  // context. Pass primaryLocationId when the caller already has the thread
  // row (string = show it immediately, null = clear immediately); omit it to
  // leave the chips untouched until the authoritative fetch lands.
  const openThread = useCallback((threadId: string, primaryLocationId?: string | null) => {
    setCurrentThreadId(threadId);
    if (primaryLocationId !== undefined) {
      setLocationIds(primaryLocationId ? [primaryLocationId] : []);
    }
    const requestId = ++openThreadRequestRef.current;
    // Browser client on purpose: a server action dispatched while the
    // click's router.push() navigation is in flight can have its response
    // swallowed (promise never settles); a plain Supabase fetch is immune.
    const supabase = createClient();
    supabase
      .from('chat_threads')
      .select('id, location_id, locations:thread_locations(location_id)')
      .eq('id', threadId)
      .single()
      .then(({ data: full, error }) => {
        if (openThreadRequestRef.current !== requestId) return; // superseded
        if (error || !full) {
          console.error('[ThreadsContext] Failed to restore thread locations:', error);
          return;
        }
        const junctionIds: string[] = (full.locations ?? [])
          .map((l: { location_id: string }) => l.location_id)
          .filter(Boolean);
        if (junctionIds.length > 0) setLocationIds(junctionIds);
        else if (full.location_id) setLocationIds([full.location_id]);
        else setLocationIds([]);
      });
  }, [setCurrentThreadId, setLocationIds]);
  // Permanently delete a thread. RLS restricts DELETE to the owner;
  // chat_messages / thread_locations / flags cascade, artifacts unlink.
  // Optimistic removal — on failure the list is re-fetched to restore truth.
  const deleteThread = useCallback(async (threadId: string) => {
    setThreads((prev) => prev.filter((t) => t.id !== threadId));
    if (currentThreadId === threadId) {
      setCurrentThreadId(undefined);
      setLocationIds([]);
    }
    const supabase = createClient();
    const { error } = await supabase.from('chat_threads').delete().eq('id', threadId);
    if (error) {
      console.error('[ThreadsContext] Failed to delete thread:', error);
      await fetchThreads();
      throw error;
    }
  }, [currentThreadId, setCurrentThreadId, setLocationIds]);

  const searchParams = useSearchParams();
  const pathname = usePathname();
  // Stabilize dependency by stringifying only the values we read from Next.js searchParams
  const searchParamsKey = React.useMemo(() => {
    const threadId = searchParams.get('threadId') || '';
    const locIds = searchParams.getAll('locationId').sort().join(',');
    const newParam = searchParams.get('new') || '';
    return `${threadId}|${locIds}|${newParam}`;
  }, [searchParams]);

  const fetchThreads = async () => {
    const supabase = createClient();
    const query = supabase
      .from("chat_threads")
      .select("*")
      .order("created_at", { ascending: false });

    // No longer filter by location IDs automatically
    // Threads should show all conversations unless explicitly filtered

    const { data, error } = await query;

    if (error) {
      console.error("Error fetching chat threads:", error);
      return;
    }
    setThreads((data || []) as ChatThread[]);
  };

  const [isInitialized, setIsInitialized] = useState(false);
  const [prevUrlLocationIds, setPrevUrlLocationIds] = useState<string[]>([]);

  // Handle 'new' parameter to start a new thread
  useEffect(() => {
    const isNewThread = searchParams.get('new') === 'true';
    if (isNewThread) {
      console.log('[ThreadsContext] New thread requested, clearing current thread ID');
      setCurrentThreadId(undefined);

      // Clear 'new' parameter from URL after processing
      const url = new URL(window.location.href);
      url.searchParams.delete('new');
      window.history.replaceState({}, '', url);
      console.log('[ThreadsContext] Cleared "new" parameter from URL');
    }
  }, [searchParamsKey]);

  // Sync location IDs from URL only when URL params actually change to something different
  useEffect(() => {
    const urlLocationIds = searchParams.getAll('locationId');
    const validUrlLocationIds = urlLocationIds.filter(id => id && id !== "null" && id !== "undefined" && id.trim() !== "");

    // Check if URL location IDs have actually changed
    const urlIdsStr = JSON.stringify(validUrlLocationIds.sort());
    const prevUrlIdsStr = JSON.stringify(prevUrlLocationIds.sort());

    if (urlIdsStr !== prevUrlIdsStr) {
      setPrevUrlLocationIds(validUrlLocationIds);

      // Only update context if URL has actual location IDs (not empty)
      if (validUrlLocationIds.length > 0) {
        const currentIdsStr = JSON.stringify(locationIds.sort());
        if (urlIdsStr !== currentIdsStr) {
          console.log('[ThreadsContext] URL location IDs changed, updating context:', validUrlLocationIds);
          setLocationIds(validUrlLocationIds);

          // Clear location IDs from URL once they're absorbed into context
          const url = new URL(window.location.href);
          url.searchParams.delete('locationId');
          window.history.replaceState({}, '', url);
          console.log('[ThreadsContext] Cleared location IDs from URL after absorbing into context');
        }
      }
      // If URL location IDs are empty, keep context as source of truth (don't clear)
    }
  }, [searchParamsKey]);

  // Initialize thread ID from URL
  useEffect(() => {
    if (isInitialized) return;

    const urlThreadId = searchParams.get('threadId');
    if (urlThreadId && urlThreadId !== "null" && urlThreadId !== "undefined") {
      console.log('[ThreadsContext] Initializing thread ID from URL:', urlThreadId);
      // openThread (not bare setCurrentThreadId) so a page reload also
      // restores the thread's location chips, which live only in state.
      openThread(urlThreadId);
    }

    setIsInitialized(true);
  }, [isInitialized, searchParamsKey, openThread]); // Only run once

  // Update URL when context changes, but only after initialization
  useEffect(() => {
    if (!isInitialized) return;

    // Only sync threadId in the URL on assistant routes
    if (!pathname || !pathname.startsWith('/assistant')) return;

    const url = new URL(window.location.href);

    if (currentThreadId) {
      url.searchParams.set('threadId', currentThreadId);
    } else {
      url.searchParams.delete('threadId');
    }

    // Don't put location IDs in URL - ThreadsContext owns them internally
    // They only come from URL initially when navigating from other pages

    // Only update if URL actually changed
    if (url.toString() !== window.location.href) {
      window.history.replaceState({}, '', url);
    }
  }, [currentThreadId, isInitialized, pathname]);

  // Create a ref to store the latest locationIds for stable subscription
  const locationIdsRef = React.useRef(locationIds);
  locationIdsRef.current = locationIds;

  useEffect(() => {
    let cancelled = false;
    fetchThreads().catch(() => {}).finally(() => {
      if (!cancelled) setIsLoading(false);
    });
    return () => { cancelled = true };
  }, []); // Only fetch threads once on mount

  // Subscribe to chat_threads changes using shared subscription system
  useSubscription(
    'chat_threads',
    (payload: RealtimePostgresChangesPayload<ChatThread>) => {
      console.log('[ThreadsContext] chat_threads event:', payload.eventType);

      if (payload.eventType === "INSERT") {
        setThreads((prev) => [
          payload.new as ChatThread,
          ...prev.filter(thread => thread.id !== payload.new.id)
        ]);
      } else if (payload.eventType === "DELETE") {
        setThreads((prev) =>
          prev.filter((thread) => thread.id !== payload.old.id)
        );
      } else if (payload.eventType === "UPDATE") {
        setThreads((prev) => {
          const updatedThreads = prev.filter(thread => thread.id !== payload.new.id);
          return [payload.new as ChatThread, ...updatedThreads];
        });
      }
    }
  );

  return (
    <ThreadsContext.Provider
      value={{
        threads,
        isLoading,
        refresh: fetchThreads,
        locationIds,
        setLocationIds,
        currentThreadId,
        setCurrentThreadId,
        openThread,
        deleteThread,
      }}
    >
      {children}
    </ThreadsContext.Provider>
  );
}

export function useThreads() {
  const context = useContext(ThreadsContext);
  if (context === undefined) {
    throw new Error("useThreads must be used within a ThreadsProvider");
  }
  return context;
}
